import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config.js';
import './ChatDrawerHeader.css';

/**
 * ChatDrawerHeader component with a conversation picker and a new-conversation button
 * @param {Object} props - Component props
 * @param {Array} props.sessions - Array of chat sessions
 * @param {string} props.currentSessionId - Currently active session ID
 * @param {function} props.onSessionSelect - Function to select a session
 * @param {function} props.onNewSession - Function to create new session
 * @param {function} props.onDeleteSession - Function to delete a session
 * @returns {JSX.Element} ChatDrawerHeader component
 */
const ChatDrawerHeader = ({
  sessions,
  currentSessionId,
  onSessionSelect,
  onNewSession,
  onDeleteSession
}) => {
  const { t } = useTranslation();
  const locale = i18n.language === 'no' ? 'no-NO' : 'en-US';
  const [isListOpen, setIsListOpen] = useState(false);
  const pickerRef = useRef(null);

  // Close the conversation list on a click outside it or on Escape
  useEffect(() => {
    if (!isListOpen) return undefined;

    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsListOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsListOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isListOpen]);

  const formatSessionTitle = (session) => {
    if (session.title) return session.title;

    // Generate title from first message or timestamp
    const firstMessage = session.messages?.[0]?.content;
    if (firstMessage) {
      return firstMessage.length > 30
        ? firstMessage.substring(0, 30) + '...'
        : firstMessage;
    }

    // Fallback to date
    const date = new Date(session.createdAt || Date.now());
    return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  };

  const currentSession = sessions.find(session => session.id === currentSessionId);
  const currentTitle = currentSession
    ? formatSessionTitle(currentSession)
    : t('sessions.newConversation');

  return (
    <div className="chat-drawer-header">
      <div className="chat-drawer-sessions" ref={pickerRef}>
        <button
          type="button"
          className="session-picker-toggle"
          onClick={() => setIsListOpen(open => !open)}
          aria-haspopup="true"
          aria-expanded={isListOpen}
          title={t('sessions.allConversations')}
        >
          <span className="session-picker-title">{currentTitle}</span>
        </button>
        {isListOpen && (
          <ul className="session-picker-menu" aria-label={t('sessions.allConversations')}>
            {sessions.map(session => (
              <li
                key={session.id}
                className={`session-picker-item ${session.id === currentSessionId ? 'active' : ''}`}
              >
                <button
                  type="button"
                  className="session-picker-select"
                  onClick={() => {
                    onSessionSelect(session.id);
                    setIsListOpen(false);
                  }}
                  aria-current={session.id === currentSessionId ? 'true' : undefined}
                >
                  <span className="session-picker-item-title">{formatSessionTitle(session)}</span>
                  {session.id === currentSessionId && (
                    <span className="session-picker-check" aria-hidden="true">✓</span>
                  )}
                </button>
                <button
                  type="button"
                  className="session-delete-horizontal"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteSession(session.id);
                  }}
                  title={t('sessions.deleteConversation')}
                  aria-label={t('sessions.deleteConversation')}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <button
        className="new-session-button-horizontal"
        onClick={onNewSession}
        title={t('sessions.newConversation')}
        aria-label={t('sessions.newConversation')}
      >
        +
      </button>
    </div>
  );
};

export default ChatDrawerHeader;
